import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Handshake, Loader2, Copy, Trash2, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { resellers as api } from "@/lib/api";
import { formatIDR } from "@/lib/format";

const empty = { name: "", whatsapp_number: "", email: "", commission_pct: 10 };

export default function Reseller() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const fetchList = async () => { setLoading(true); try { setRows(await api.list()); } catch (err) { console.warn("Resellers load failed:", err); } setLoading(false); };
  useEffect(() => { fetchList(); }, []);

  const create = async () => {
    if (!form.name || !form.whatsapp_number) { toast.error("Nama & WA wajib"); return; }
    try { await api.create(form); toast.success("Reseller ditambahkan"); setOpen(false); setForm(empty); fetchList(); }
    catch { toast.error("Gagal"); }
  };
  const remove = async (id) => { if (!window.confirm("Hapus?")) return; await api.remove(id); toast.success("Dihapus"); fetchList(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Disalin"); };

  const totalCommission = rows.reduce((s, r) => s + (r.commission_earned || 0), 0);
  const pendingCommission = rows.reduce((s, r) => s + Math.max(0, (r.commission_earned || 0) - (r.commission_paid || 0)), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Reseller Network</h1>
          <p className="text-sm text-gray-500 mt-1">Bangun tim reseller Anda. Setiap reseller punya kode unik & tracking komisi otomatis.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-reseller-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Reseller</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Daftarkan Reseller</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nama Reseller</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reseller-name" /></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} data-testid="reseller-wa" /></div>
              <div><Label>Email (opsional)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reseller-email" /></div>
              <div><Label>Komisi (%)</Label><Input type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })} data-testid="reseller-commission" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-reseller-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="border-gray-200"><CardContent className="p-5"><div className="text-xs text-gray-500 font-bold uppercase tracking-wider">Reseller</div><div className="mt-1 font-heading text-2xl font-bold text-gray-900">{rows.length}</div></CardContent></Card>
        <Card className="border-gray-200"><CardContent className="p-5"><div className="text-xs text-gray-500 font-bold uppercase tracking-wider">Komisi Diperoleh</div><div className="mt-1 font-heading text-2xl font-bold text-emerald-600">{formatIDR(totalCommission)}</div></CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/30"><CardContent className="p-5"><div className="text-xs text-amber-700 font-bold uppercase tracking-wider">Komisi Pending</div><div className="mt-1 font-heading text-2xl font-bold text-amber-700">{formatIDR(pendingCommission)}</div></CardContent></Card>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center gap-2">
          <Trophy className="text-amber-500" size={18} />
          <h3 className="font-heading font-semibold text-gray-900">Leaderboard Reseller</h3>
        </div>
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <Handshake className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada reseller</h3>
              <p className="mt-1 text-sm text-gray-500">Rekrut reseller pertama Anda dan mulai jaringan penjualan.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Rank</th>
                    <th className="px-4 py-3">Nama</th>
                    <th className="px-4 py-3">Kode</th>
                    <th className="px-4 py-3">Orders</th>
                    <th className="px-4 py-3">Revenue</th>
                    <th className="px-4 py-3">Komisi %</th>
                    <th className="px-4 py-3">Earned</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r, i) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-gray-200 text-gray-700" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>{i + 1}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{r.name}</div>
                        <div className="text-xs text-gray-500">{r.whatsapp_number}</div>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => copy(r.reseller_code)} className="font-mono text-emerald-700 font-semibold hover:underline flex items-center gap-1"><span>{r.reseller_code}</span><Copy size={10} /></button>
                      </td>
                      <td className="px-4 py-3 text-gray-900 font-semibold">{r.total_orders || 0}</td>
                      <td className="px-4 py-3 text-gray-900 font-semibold">{formatIDR(r.total_revenue)}</td>
                      <td className="px-4 py-3"><Badge className="bg-blue-50 text-blue-700 border-blue-200 border">{r.commission_pct}%</Badge></td>
                      <td className="px-4 py-3 text-emerald-600 font-semibold">{formatIDR(r.commission_earned)}</td>
                      <td className="px-4 py-3"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(r.id)}><Trash2 size={14} className="text-red-500" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
